// Inline Supabase action execution component
// Shows animation during execution, success/error state, and auto-retry

import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Database, Table, Shield, Code, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PxMark } from '@/components/PxMark';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';

export interface SupabaseAction {
    type: 'supabase-sql' | 'supabase-create-table' | 'supabase-alter-table' | 'supabase-policy' | 'supabase-edge-function' | 'supabase-logs' | 'supabase-schema';
    content: string;
    name?: string;
    table?: string;
}

interface SupabaseInlineActionProps {
    action: SupabaseAction;
    pixelProjectId?: string;
    autoExecute?: boolean;
    onComplete?: (success: boolean, result?: any) => void;
}

type ExecutionState = 'pending' | 'executing' | 'success' | 'error' | 'retrying';

const MAX_RETRIES = 3;

const getActionIcon = (type: SupabaseAction['type']) => {
    switch (type) {
        case 'supabase-sql': return Database;
        case 'supabase-create-table': return Table;
        case 'supabase-alter-table': return Table;
        case 'supabase-policy': return Shield;
        case 'supabase-edge-function': return Code;
        case 'supabase-logs': return FileText;
        case 'supabase-schema': return Database;
        default: return Database;
    }
};

const getActionLabel = (action: SupabaseAction) => {
    switch (action.type) {
        case 'supabase-sql': return 'Exécution SQL';
        case 'supabase-create-table': return `Créer table ${action.name || ''}`;
        case 'supabase-alter-table': return `Modifier table ${action.name || ''}`;
        case 'supabase-policy': return `Policy RLS ${action.table || ''}`;
        case 'supabase-edge-function': return `Edge Function ${action.name || ''}`;
        case 'supabase-logs': return 'Récupération logs';
        case 'supabase-schema': return 'Récupération schéma';
        default: return 'Action Supabase';
    }
};

export function SupabaseInlineAction({
    action,
    pixelProjectId,
    autoExecute = false,
    onComplete
}: SupabaseInlineActionProps) {
    const [state, setState] = useState<ExecutionState>(autoExecute ? 'executing' : 'pending');
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    const { executeSql, isConnected } = useSupabaseMCP(pixelProjectId);

    const Icon = getActionIcon(action.type);
    const label = getActionLabel(action);

    const execute = useCallback(async () => {
        if (!isConnected) {
            setError('Supabase non connecté');
            setState('error');
            return;
        }

        setState('executing');
        setError(null);

        try {
            // Execute based on action type
            let execResult;

            if (action.type === 'supabase-sql' || action.type === 'supabase-create-table' || action.type === 'supabase-alter-table' || action.type === 'supabase-policy') {
                execResult = await executeSql(action.content);
            } else {
                // For now, other actions are not supported
                execResult = { success: false, error: 'Action type not yet supported for inline execution' };
            }

            if (execResult?.success) {
                setState('success');
                setResult(execResult.data);
                onComplete?.(true, execResult.data);
            } else {
                throw new Error(execResult?.error || 'Échec de l\'exécution');
            }
        } catch (err: any) {
            const errorMsg = err.message || 'Erreur inconnue';
            setError(errorMsg);

            // Auto-retry logic - check current retryCount directly
            setRetryCount(currentCount => {
                if (currentCount < MAX_RETRIES - 1) {
                    setState('retrying');
                    // Retry after 1 second with incremented count
                    setTimeout(() => {
                        setRetryCount(c => c + 1);
                        execute();
                    }, 1000);
                    return currentCount; // Don't increment yet, setTimeout will do it
                } else {
                    setState('error');
                    onComplete?.(false, { error: errorMsg });
                    return currentCount;
                }
            });
        }
    }, [action, executeSql, isConnected, onComplete]); // Removed retryCount from deps

    // Auto-execute on mount if enabled
    useEffect(() => {
        if (autoExecute && state === 'executing' && retryCount === 0) {
            execute();
        }
    }, [autoExecute, execute, state, retryCount]);

    const handleRetry = () => {
        setRetryCount(0);
        execute();
    };

    const handleExecute = () => {
        execute();
    };

    return (
        <div className="flex items-center gap-2.5 px-3 py-2 border border-border bg-card transition-colors">
            {/* Dot d'état sémantique — seul rond autorisé (6px) */}
            {state === 'executing' || state === 'retrying' ? (
                <PxMark state="generating" size={12} label="Exécution en cours" />
            ) : (
                <span
                    className={cn(
                        "h-[6px] w-[6px] rounded-full shrink-0",
                        state === 'pending' && "bg-faint",
                        state === 'success' && "bg-ok",
                        state === 'error' && "bg-err"
                    )}
                />
            )}

            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-foreground truncate">
                        {label}
                    </span>
                    {state === 'retrying' && (
                        <span className="font-mono text-[10px] text-warn shrink-0">
                            tentative {retryCount + 1}/{MAX_RETRIES}
                        </span>
                    )}
                </div>
                {error && state === 'error' && (
                    <p className="font-mono text-[10px] text-err truncate mt-0.5">{error}</p>
                )}
                {state === 'success' && result && (
                    <p className="font-mono text-[10px] text-ok truncate mt-0.5">
                        {typeof result === 'object' ? JSON.stringify(result).slice(0, 50) + '…' : result}
                    </p>
                )}
            </div>

            {/* Actions */}
            {state === 'pending' && (
                <button
                    onClick={handleExecute}
                    className="px-3 py-1.5 text-xs font-medium bg-card border border-border text-foreground hover:bg-secondary transition-colors"
                >
                    Exécuter
                </button>
            )}
            {state === 'error' && (
                <button
                    onClick={handleRetry}
                    className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    title="Réessayer"
                >
                    <RefreshCw className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}
